import { Collapse, Table as MaterialTable, TableBody } from '@material-ui/core';
import RelativeTooltip from 'components/relative-tooltip';
import { RelativeTooltipContent } from 'components/relative-tooltip/relative-tooltip-content';
import SearchError from 'components/search-error';
import React, { forwardRef, useRef, useState } from 'react';
import { TableScrollbar } from '../table-scrollbar/table-scrollbar';
import { LoadMore } from './load-more/load-more';
import { TableHeader } from './table-header/table-header';
import { TableProps } from './table-props';
import { TableStickyHeader } from './table-sticky-header/table-sticky-header';
import './table.scss';

/**
 * SG custom table.
 */
export const Table: React.FC<TableProps> = forwardRef((props: TableProps, ref) => {
  const {
    error,
    loadedNoun,
    loading,
    loadingMore,
    resultsCount,
    retryComponentProps = { retry: () => { } },
    showHeader = true,
    showLoadMore = true,
    sortBy = 'label',
    totalCount,
    getHeaderTooltipContent,
    onLoadMore,
  } = props;

  const [openTooltipId, setOpenTooltipId] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState<RelativeTooltipContent>();
  const tableHeadRef = useRef<HTMLTableSectionElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLTableElement>(null);

  /**
   * Fires the `onLoadMore` callback.
   */
  const loadMore = () => {
    if (onLoadMore) {
      onLoadMore();
    }
  };

  /**
   * Gets the tooltip content then shows the tooltip.
   * @param show
   * @param id
   */
  const infoIconClick = (show: boolean, id: string) => {
    if (show && getHeaderTooltipContent) {
      const content = getHeaderTooltipContent(id);
      setTooltipContent(content);
    }
    setOpenTooltipId(show ? id : '');
    setShowTooltip(show);
  };

  /**
   * Closes the tooltip and resets the open id.
   */
  const closeTooltip = () => {
    setOpenTooltipId('');
    setShowTooltip(false);
  };

  return (
    <div className="sg-table" id={props.id}>
      {!loading && (
        <>
          {props.stickyHeader && (
            <TableStickyHeader
              ref={stickyHeaderRef}
              tableHeadRef={tableHeadRef}
              columns={props.columns}
              onHeaderSelected={props.onHeaderSelected}
              enableSort={props.enableSort}
              tableRef={tableContainerRef}
              sortBy={sortBy}
            />
          )}
          <div className="row-32">
            <Collapse
              in={showTooltip}
              mountOnEnter
              unmountOnExit
            >
              <RelativeTooltip
                content={tooltipContent}
                onClose={closeTooltip}
              />
            </Collapse>
          </div>
          <div
            className={`sg-table__wrapper ${props.scrollShadow ? 'table-scroll-shadow' : ''}`}
            ref={tableContainerRef}
          >
            <MaterialTable
              className={`sg-table__table ${props.className || ''}`}
              stickyHeader
              ref={ref}
            >
              {showHeader && (
                <TableHeader
                  ref={tableHeadRef}
                  columns={props.columns}
                  headerSelected={props.onHeaderSelected}
                  enableSort={props.enableSort}
                  openTooltipId={openTooltipId}
                  onInfoIconClick={infoIconClick}
                  showInfoIcon
                  sortBy={sortBy}
                />
              )}
              <TableBody className="sg-table__body">
                {props.children}
              </TableBody>
            </MaterialTable>
          </div>
          {props.isVerticallyScrolled && <TableScrollbar tableRef={tableContainerRef} stickyHeaderRef={stickyHeaderRef} />}
        </>
      )}
      {/** Load more button */}
      {!loading && showLoadMore
        && (
          <LoadMore
            loading={loadingMore}
            loadedResults={resultsCount}
            totalCount={totalCount}
            loadedNoun={loadedNoun}
            onLoadMore={loadMore}
          />
        )}
      {/** Retry/try again component */}
      {error && (
        <SearchError
          {...retryComponentProps}
          size={resultsCount ? 'small' : 'large'}
        />
      )}
    </div>
  );
});
